import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { AppLayout, GlassCard, PageHeader, LoadingSpinner } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { 
  Shield, Lock, FileText, Award, ClipboardCheck, TrendingUp,
  Phone, Calendar, Target, Star, ChevronRight, Plus, Trash2,
  AlertTriangle, CheckCircle, Building, GraduationCap, Briefcase,
  Eye, Download, Clock, User, Upload, Brain, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProfileIntelligencePage() {
  const { user, getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [profileStrength, setProfileStrength] = useState(null);
  const [companyMatches, setCompanyMatches] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [security, setSecurity] = useState(null);
  const [certifications, setCertifications] = useState([]);
  const [testScores, setTestScores] = useState([]);
  const [personalitySummary, setPersonalitySummary] = useState(null);
  
  // Form states
  const [showAddCert, setShowAddCert] = useState(false);
  const [showAddScore, setShowAddScore] = useState(false);
  const [newCert, setNewCert] = useState({ name: '', issuer: '', date_earned: '' });
  const [newScore, setNewScore] = useState({ test_type: 'SAT', score: '', date_taken: '' });
  const [phoneNumber, setPhoneNumber] = useState('');
  const [analyzingTranscript, setAnalyzingTranscript] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const headers = { ...getAuthHeader(), withCredentials: true };
      
      const [strengthRes, matchesRes, timelineRes, securityRes, certsRes, scoresRes, personalityRes] = await Promise.all([
        axios.get(`${API}/profile/strength`, headers),
        axios.get(`${API}/profile/all-matches`, headers),
        axios.get(`${API}/profile/progress-timeline`, headers),
        axios.get(`${API}/profile/security-info`, headers),
        axios.get(`${API}/certifications`, headers),
        axios.get(`${API}/test-scores`, headers),
        axios.get(`${API}/profile/personality-summary`, headers)
      ]);
      
      setProfileStrength(strengthRes.data);
      setCompanyMatches(matchesRes.data.matches);
      setTimeline(timelineRes.data.timeline);
      setSecurity(securityRes.data);
      setCertifications(certsRes.data.certifications);
      setTestScores(scoresRes.data.test_scores);
      setPersonalitySummary(personalityRes.data);
    } catch (error) {
      console.error('Failed to fetch profile data:', error);
      toast.error('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const addCertification = async () => {
    if (!newCert.name || !newCert.issuer || !newCert.date_earned) {
      toast.error('Please fill all required fields');
      return;
    }
    try {
      await axios.post(`${API}/certifications`, newCert, {
        ...getAuthHeader(),
        withCredentials: true
      });
      toast.success('Certification added!');
      setShowAddCert(false);
      setNewCert({ name: '', issuer: '', date_earned: '' });
      fetchAllData();
    } catch (error) {
      toast.error('Failed to add certification');
    }
  };

  const addTestScore = async () => {
    if (!newScore.test_type || !newScore.score || !newScore.date_taken) {
      toast.error('Please fill all required fields');
      return;
    }
    try {
      await axios.post(`${API}/test-scores`, {
        ...newScore,
        score: parseInt(newScore.score)
      }, {
        ...getAuthHeader(),
        withCredentials: true
      });
      toast.success('Test score added!');
      setShowAddScore(false);
      setNewScore({ test_type: 'SAT', score: '', date_taken: '' });
      fetchAllData();
    } catch (error) {
      toast.error('Failed to add test score');
    }
  };

  const analyzeTranscript = async () => {
    setAnalyzingTranscript(true);
    try {
      const response = await axios.post(`${API}/ai/analyze-transcript`, {}, {
        ...getAuthHeader(),
        withCredentials: true,
        timeout: 60000
      });
      toast.success('Transcript analyzed! Your profile has been updated.');
      setProfileStrength(response.data.profile_strength);
      fetchAllData();
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error(
          <div>
            <p className="font-medium">No transcript found</p>
            <p className="text-sm">Upload a transcript in Document Vault first, then come back to analyze it.</p>
          </div>,
          {
            duration: 5000,
            action: {
              label: 'Go to Vault',
              onClick: () => window.location.href = '/app/vault'
            }
          }
        );
      } else {
        toast.error('Failed to analyze transcript. Please try again.');
      }
    } finally {
      setAnalyzingTranscript(false);
    }
  };

  const verifyPhone = async () => {
    if (!phoneNumber) {
      toast.error('Please enter a phone number');
      return;
    }
    try {
      await axios.post(`${API}/profile/verify-phone`, {
        phone_number: phoneNumber
      }, {
        ...getAuthHeader(),
        withCredentials: true
      });
      toast.success('Phone number saved!');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to save phone number');
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: TrendingUp },
    { id: 'personality', label: 'Personality', icon: Brain },
    { id: 'achievements', label: 'Achievements', icon: Award },
    { id: 'matches', label: 'Career Matches', icon: Target },
    { id: 'timeline', label: 'Progress', icon: Clock },
    { id: 'security', label: 'Security', icon: Shield }
  ];

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PageHeader 
        title="Profile Intelligence"
        description="Track your progress and see how your profile matches career goals"
      />

      {/* Profile Strength Banner */}
      <div className="mb-6 p-6 bg-gradient-to-r from-blue-900/50 to-purple-900/50 rounded-xl border border-blue-500/30">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
              <span className="text-3xl font-bold text-white">{profileStrength?.percentage || 0}%</span>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Profile Strength</h2>
              <p className="text-slate-300">
                {profileStrength?.percentage >= 80 ? 'Excellent! Your profile is very strong.' :
                 profileStrength?.percentage >= 60 ? 'Good progress! Add more details to improve.' :
                 profileStrength?.percentage >= 40 ? 'Getting there! Complete more sections.' :
                 'Just getting started. Add information to boost your profile.'}
              </p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button 
              onClick={analyzeTranscript}
              disabled={analyzingTranscript}
              className="bg-purple-600 hover:bg-purple-700"
              title="Analyze uploaded transcript from Document Vault"
            >
              {analyzingTranscript ? <LoadingSpinner size="sm" /> : <FileText className="w-4 h-4 mr-2" />}
              Analyze Transcript
            </Button>
            <Link to="/app/vault">
              <Button variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-700">
                <Upload className="w-4 h-4 mr-2" />
                Upload to Vault
              </Button>
            </Link>
          </div>
        </div>
        
        {/* Progress bar */}
        <div className="mt-4">
          <Progress value={profileStrength?.percentage || 0} className="h-3" />
          <div className="flex justify-between text-xs text-slate-300 mt-2">
            <span>Missing: {profileStrength?.missing?.join(', ') || 'None'}</span>
            <span>{profileStrength?.score || 0} / {profileStrength?.max_score || 100} points</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg text-sm flex items-center gap-2 transition-all ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-800 text-slate-300 hover:text-white'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Profile Breakdown */}
          <GlassCard>
            <h3 className="font-outfit text-lg font-semibold text-white mb-4">Profile Breakdown</h3>
            <div className="space-y-3">
              {Object.entries(profileStrength?.breakdown || {}).map(([key, value]) => (
                <div key={key} className="flex items-center justify-between">
                  <span className="text-slate-300 capitalize">{key.replace(/_/g, ' ')}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-emerald-400">+{value}</span>
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                  </div>
                </div>
              ))}
              {profileStrength?.missing?.map((key) => (
                <div key={key} className="flex items-center justify-between">
                  <span className="text-slate-300 capitalize">{key.replace(/_/g, ' ')}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-slate-300">+0</span>
                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                  </div>
                </div>
              ))}
            </div>
          </GlassCard>

          {/* Quick Actions */}
          <GlassCard>
            <h3 className="font-outfit text-lg font-semibold text-white mb-4">Boost Your Profile</h3>
            <div className="space-y-3">
              <button 
                onClick={() => setShowAddCert(true)}
                className="w-full p-3 bg-slate-800/50 rounded-lg flex items-center gap-3 hover:bg-slate-800 transition-colors text-left"
              >
                <Award className="w-5 h-5 text-amber-400" />
                <div className="flex-1">
                  <p className="text-white text-sm font-medium">Add Certification</p>
                  <p className="text-slate-300 text-xs">Coursera, LinkedIn Learning, etc.</p>
                </div>
                <Plus className="w-4 h-4 text-slate-300" />
              </button>
              
              <button 
                onClick={() => setShowAddScore(true)}
                className="w-full p-3 bg-slate-800/50 rounded-lg flex items-center gap-3 hover:bg-slate-800 transition-colors text-left"
              >
                <ClipboardCheck className="w-5 h-5 text-blue-400" />
                <div className="flex-1">
                  <p className="text-white text-sm font-medium">Add Test Score</p>
                  <p className="text-slate-300 text-xs">SAT, ACT, ASVAB, AP Exams</p>
                </div>
                <Plus className="w-4 h-4 text-slate-300" />
              </button>
              
              <div className="p-3 bg-slate-800/50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Phone className="w-5 h-5 text-emerald-400" />
                  <p className="text-white text-sm font-medium">Add Phone for Reminders</p>
                </div>
                <div className="flex gap-2">
                  <Input
                    placeholder="+1 (555) 123-4567"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    className="input-dark text-sm"
                  />
                  <Button onClick={verifyPhone} size="sm" className="bg-emerald-600">Save</Button>
                </div>
              </div>
            </div>
          </GlassCard>

          {/* Transcript Data */}
          {user?.transcript_data && (
            <GlassCard className="lg:col-span-2">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-outfit text-lg font-semibold text-white">Transcript Analysis</h3>
                <span className="px-3 py-1 bg-emerald-500/20 text-emerald-400 rounded-full text-xs">AI Analyzed</span>
              </div>
              <div className="grid md:grid-cols-4 gap-4">
                <div className="p-4 bg-slate-800/50 rounded-lg text-center">
                  <p className="text-3xl font-bold text-emerald-400">{user.transcript_data.gpa || 'N/A'}</p>
                  <p className="text-slate-300 text-sm">GPA</p>
                </div>
                <div className="p-4 bg-slate-800/50 rounded-lg text-center">
                  <p className="text-3xl font-bold text-blue-400">{user.transcript_data.courses?.length || 0}</p>
                  <p className="text-slate-300 text-sm">Courses</p>
                </div>
                <div className="p-4 bg-slate-800/50 rounded-lg text-center">
                  <p className="text-3xl font-bold text-amber-400">{user.transcript_data.honors_ap_courses?.length || 0}</p>
                  <p className="text-slate-300 text-sm">AP/Honors</p>
                </div>
                <div className="p-4 bg-slate-800/50 rounded-lg text-center">
                  <p className="text-lg font-bold text-purple-400">{user.transcript_data.grade_level || 'N/A'}</p>
                  <p className="text-slate-300 text-sm">Grade Level</p>
                </div>
              </div>
              {user.transcript_data.strengths?.length > 0 && (
                <div className="mt-4">
                  <p className="text-slate-300 text-sm mb-2">Strengths:</p>
                  <div className="flex flex-wrap gap-2">
                    {user.transcript_data.strengths.map((s, i) => (
                      <span key={i} className="px-3 py-1 bg-emerald-500/20 text-emerald-400 rounded-full text-sm">{s}</span>
                    ))}
                  </div>
                </div>
              )}
            </GlassCard>
          )}
        </div>
      )}

      {/* Personality Tab - NEW */}
      {activeTab === 'personality' && (
        <div className="space-y-6">
          {personalitySummary?.has_personality ? (
            <>
              {/* Main Personality Card */}
              <GlassCard>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl flex items-center justify-center">
                    <Brain className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h2 className="font-outfit text-2xl font-bold text-white">
                      {personalitySummary.personality_type?.name}
                    </h2>
                    <p className="text-slate-300">{personalitySummary.personality_type?.description}</p>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4 mb-6">
                  <div className="p-4 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                    <h3 className="text-emerald-400 font-medium mb-2 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" /> Strengths
                    </h3>
                    <ul className="space-y-1">
                      {personalitySummary.personality_type?.strengths?.map((s, i) => (
                        <li key={i} className="text-slate-300 text-sm">• {s}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="p-4 bg-amber-500/10 rounded-lg border border-amber-500/20">
                    <h3 className="text-amber-400 font-medium mb-2 flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" /> Growth Areas
                    </h3>
                    <ul className="space-y-1">
                      {personalitySummary.personality_type?.growth_areas?.map((g, i) => (
                        <li key={i} className="text-slate-300 text-sm">• {g}</li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Learning Style */}
                {personalitySummary.learning_style && (
                  <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/20">
                    <h3 className="text-blue-400 font-medium mb-2">
                      Learning Style: {personalitySummary.learning_style.primary?.charAt(0).toUpperCase() + personalitySummary.learning_style.primary?.slice(1)}
                    </h3>
                    <ul className="space-y-1">
                      {personalitySummary.learning_style.tips?.map((tip, i) => (
                        <li key={i} className="text-slate-300 text-sm">• {tip}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </GlassCard>

              {/* Work Environment */}
              <div className="grid md:grid-cols-2 gap-6">
                <GlassCard>
                  <h3 className="font-outfit text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                    Ideal Environment
                  </h3>
                  <div className="p-4 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                    <p className="text-slate-300">{personalitySummary.work_environment?.ideal}</p>
                  </div>
                  {personalitySummary.work_environment?.key_factors && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {personalitySummary.work_environment.key_factors.map((factor, i) => (
                        <span key={i} className="px-3 py-1 bg-emerald-500/20 text-emerald-300 rounded-full text-sm">
                          {factor}
                        </span>
                      ))}
                    </div>
                  )}
                </GlassCard>

                <GlassCard>
                  <h3 className="font-outfit text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-400" />
                    Environments to Avoid
                  </h3>
                  <div className="p-4 bg-red-500/10 rounded-lg border border-red-500/20">
                    <p className="text-slate-300">{personalitySummary.work_environment?.avoid}</p>
                  </div>
                </GlassCard>
              </div>

              {/* Trait Scores Detailed */}
              {personalitySummary.trait_scores && (
                <GlassCard>
                  <h3 className="font-outfit text-lg font-semibold text-white mb-4">Trait Analysis</h3>
                  <div className="space-y-4">
                    {Object.entries(personalitySummary.trait_scores).map(([trait, score]) => (
                      <div key={trait}>
                        <div className="flex justify-between mb-1">
                          <span className="text-slate-300 capitalize">{trait}</span>
                          <span className="text-purple-400 font-semibold">{score}%</span>
                        </div>
                        <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full ${
                              score >= 70 ? 'bg-gradient-to-r from-emerald-500 to-emerald-400' :
                              score >= 40 ? 'bg-gradient-to-r from-blue-500 to-blue-400' :
                              'bg-gradient-to-r from-amber-500 to-amber-400'
                            }`}
                            style={{ width: `${score}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </GlassCard>
              )}

              {/* Career Tags */}
              {personalitySummary.career_tags?.length > 0 && (
                <GlassCard>
                  <h3 className="font-outfit text-lg font-semibold text-white mb-4">Career Categories That Fit You</h3>
                  <div className="flex flex-wrap gap-2">
                    {personalitySummary.career_tags.map((tag, i) => (
                      <span key={i} className="px-4 py-2 bg-purple-500/20 text-purple-300 rounded-lg text-sm capitalize">
                        {tag.replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>
                </GlassCard>
              )}

              <div className="text-center">
                <Link to="/app/assessment">
                  <Button variant="outline" className="border-purple-500/50 text-purple-300">
                    <Brain className="w-4 h-4 mr-2" />
                    Retake Personality Quiz
                  </Button>
                </Link>
                <p className="text-slate-300 text-sm mt-2">
                  Last updated: {personalitySummary.updated_at ? new Date(personalitySummary.updated_at).toLocaleDateString() : 'Unknown'}
                </p>
              </div>
            </>
          ) : (
            <GlassCard className="text-center py-12">
              <Brain className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No Personality Profile Yet</h3>
              <p className="text-slate-300 mb-6 max-w-md mx-auto">
                Take the personality quiz to discover your unique traits, ideal work environments, and career matches tailored to you.
              </p>
              <Link to="/app/assessment">
                <Button className="bg-purple-600 hover:bg-purple-700">
                  <Brain className="w-4 h-4 mr-2" />
                  Start Personality Quiz
                </Button>
              </Link>
            </GlassCard>
          )}
        </div>
      )}

      {/* Achievements Tab */}
      {activeTab === 'achievements' && (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Certifications */}
          <GlassCard>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-outfit text-lg font-semibold text-white">Certifications</h3>
              <Button onClick={() => setShowAddCert(true)} size="sm" className="bg-amber-600">
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>
            {certifications.length > 0 ? (
              <div className="space-y-3">
                {certifications.map((cert) => (
                  <div key={cert.id} className="p-3 bg-slate-800/50 rounded-lg flex items-start gap-3">
                    <Award className="w-5 h-5 text-amber-400 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-white font-medium">{cert.name}</p>
                      <p className="text-slate-300 text-sm">{cert.issuer}</p>
                      <p className="text-slate-300 text-xs">{cert.date_earned}</p>
                    </div>
                    {cert.credential_url && (
                      <a href={cert.credential_url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">
                        <Eye className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Award className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                <p className="text-slate-300">No certifications yet</p>
                <p className="text-slate-300 text-sm">Add your Coursera, LinkedIn Learning, or other certs</p>
              </div>
            )}
          </GlassCard>

          {/* Test Scores */}
          <GlassCard>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-outfit text-lg font-semibold text-white">Test Scores</h3>
              <Button onClick={() => setShowAddScore(true)} size="sm" className="bg-blue-600">
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>
            {testScores.length > 0 ? (
              <div className="space-y-3">
                {testScores.map((score) => (
                  <div key={score.id} className="p-3 bg-slate-800/50 rounded-lg flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <ClipboardCheck className="w-5 h-5 text-blue-400" />
                      <div>
                        <p className="text-white font-medium">{score.test_type}</p>
                        <p className="text-slate-300 text-xs">{score.date_taken}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-blue-400">{score.score}</p>
                      {score.max_score && <p className="text-slate-300 text-xs">/ {score.max_score}</p>}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <ClipboardCheck className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                <p className="text-slate-300">No test scores yet</p>
                <p className="text-slate-300 text-sm">Add SAT, ACT, ASVAB, or AP scores</p>
              </div>
            )}
          </GlassCard>
        </div>
      )}

      {/* Career Matches Tab */}
      {activeTab === 'matches' && (
        <div className="grid lg:grid-cols-2 gap-6">
          {companyMatches && Object.entries(companyMatches).map(([type, match]) => (
            <GlassCard key={type} className={match.match_score >= 70 ? 'border-emerald-500/30' : ''}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    type === 'FAANG' ? 'bg-purple-500/20' :
                    type === 'Big4_Consulting' ? 'bg-blue-500/20' :
                    type === 'Healthcare_Top' ? 'bg-emerald-500/20' :
                    'bg-amber-500/20'
                  }`}>
                    {type === 'FAANG' ? <Building className="w-5 h-5 text-purple-400" /> :
                     type === 'Big4_Consulting' ? <Briefcase className="w-5 h-5 text-blue-400" /> :
                     type === 'Healthcare_Top' ? <GraduationCap className="w-5 h-5 text-emerald-400" /> :
                     <Target className="w-5 h-5 text-amber-400" />}
                  </div>
                  <div>
                    <h4 className="text-white font-medium">{type.replace(/_/g, ' ')}</h4>
                    <p className="text-slate-300 text-xs">Career pathway match</p>
                  </div>
                </div>
                <div className={`text-2xl font-bold ${
                  match.match_score >= 70 ? 'text-emerald-400' :
                  match.match_score >= 50 ? 'text-amber-400' :
                  'text-red-400'
                }`}>
                  {match.match_score}%
                </div>
              </div>
              
              {match.requirements?.length > 0 && (
                <div className="mb-3">
                  <p className="text-slate-300 text-xs mb-2">✓ Requirements Met:</p>
                  {match.requirements.map((req, i) => (
                    <p key={i} className="text-emerald-400 text-sm flex items-center gap-2">
                      <CheckCircle className="w-3 h-3" /> {req}
                    </p>
                  ))}
                </div>
              )}
              
              {match.gaps?.length > 0 && (
                <div>
                  <p className="text-slate-300 text-xs mb-2">⚠ Areas to Improve:</p>
                  {match.gaps.map((gap, i) => (
                    <p key={i} className="text-amber-400 text-sm flex items-center gap-2">
                      <AlertTriangle className="w-3 h-3" /> {gap}
                    </p>
                  ))}
                </div>
              )}
            </GlassCard>
          ))}
        </div>
      )}

      {/* Timeline Tab */}
      {activeTab === 'timeline' && (
        <GlassCard>
          <h3 className="font-outfit text-lg font-semibold text-white mb-6">Your Journey</h3>
          {timeline.length > 0 ? (
            <div className="relative">
              <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-slate-700" />
              <div className="space-y-4">
                {timeline.map((event, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.1 }}
                    className="flex items-start gap-4 pl-8 relative"
                  >
                    <div className={`absolute left-2 w-4 h-4 rounded-full ${
                      event.type === 'certification' ? 'bg-amber-500' :
                      event.type === 'test_score' ? 'bg-blue-500' :
                      event.type === 'milestone' ? 'bg-emerald-500' :
                      event.type === 'achievement' ? 'bg-purple-500' :
                      'bg-slate-500'
                    }`} />
                    <div className="flex-1 p-3 bg-slate-800/50 rounded-lg">
                      <p className="text-white font-medium">{event.title}</p>
                      <p className="text-slate-300 text-xs">{event.date?.split('T')[0]}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <Clock className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-300">Your journey is just beginning</p>
              <p className="text-slate-300 text-sm">Complete actions to build your timeline</p>
            </div>
          )}
        </GlassCard>
      )}

      {/* Security Tab */}
      {activeTab === 'security' && security && (
        <div className="grid lg:grid-cols-2 gap-6">
          <GlassCard>
            <div className="flex items-center gap-3 mb-6">
              <Shield className="w-8 h-8 text-emerald-400" />
              <div>
                <h3 className="font-outfit text-lg font-semibold text-white">Your Data is Protected</h3>
                <p className="text-slate-300 text-sm">We take your privacy seriously</p>
              </div>
            </div>
            
            <div className="space-y-3">
              {security.security_features?.map((feature, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 bg-emerald-500/10 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                  <div>
                    <p className="text-white text-sm font-medium">{feature.feature}</p>
                    <p className="text-slate-300 text-xs">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </GlassCard>

          <GlassCard>
            <h3 className="font-outfit text-lg font-semibold text-white mb-4">Data Stored</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-slate-800/50 rounded-lg">
                <span className="text-slate-300">Documents</span>
                <span className="text-blue-400 font-bold">{security.data_stored?.documents || 0}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-slate-800/50 rounded-lg">
                <span className="text-slate-300">Certifications</span>
                <span className="text-amber-400 font-bold">{security.data_stored?.certifications || 0}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-slate-800/50 rounded-lg">
                <span className="text-slate-300">Test Scores</span>
                <span className="text-emerald-400 font-bold">{security.data_stored?.test_scores || 0}</span>
              </div>
            </div>
            
            <div className="mt-6 p-4 bg-slate-800/30 rounded-lg">
              <h4 className="text-white font-medium mb-2">Data Policy</h4>
              <p className="text-slate-300 text-sm">{security.data_retention?.policy}</p>
              <p className="text-slate-300 text-xs mt-2">{security.data_retention?.deletion}</p>
            </div>
          </GlassCard>
        </div>
      )}

      {/* Add Certification Modal */}
      <AnimatePresence>
        {showAddCert && (
          <div className="fixed inset-0 bg-[#0a1729]/50 flex items-center justify-center z-50" onClick={() => setShowAddCert(false)}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="glass-card p-6 max-w-md w-full mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="font-outfit text-lg font-semibold text-white mb-4">Add Certification</h3>
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-slate-300 block mb-1">Certification Name *</label>
                  <Input
                    placeholder="e.g., Google IT Support Professional"
                    value={newCert.name}
                    onChange={(e) => setNewCert({...newCert, name: e.target.value})}
                    className="input-dark"
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-300 block mb-1">Issuer *</label>
                  <Input
                    placeholder="e.g., Coursera, LinkedIn Learning"
                    value={newCert.issuer}
                    onChange={(e) => setNewCert({...newCert, issuer: e.target.value})}
                    className="input-dark"
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-300 block mb-1">Date Earned *</label>
                  <Input
                    type="date"
                    value={newCert.date_earned}
                    onChange={(e) => setNewCert({...newCert, date_earned: e.target.value})}
                    className="input-dark"
                  />
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowAddCert(false)} className="border-slate-700">Cancel</Button>
                  <Button onClick={addCertification} className="flex-1 bg-amber-600 hover:bg-amber-700">Add Certification</Button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Add Test Score Modal */}
      <AnimatePresence>
        {showAddScore && (
          <div className="fixed inset-0 bg-[#0a1729]/50 flex items-center justify-center z-50" onClick={() => setShowAddScore(false)}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="glass-card p-6 max-w-md w-full mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="font-outfit text-lg font-semibold text-white mb-4">Add Test Score</h3>
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-slate-300 block mb-1">Test Type *</label>
                  <select
                    value={newScore.test_type}
                    onChange={(e) => setNewScore({...newScore, test_type: e.target.value})}
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white"
                  >
                    <option value="SAT">SAT</option>
                    <option value="ACT">ACT</option>
                    <option value="PSAT">PSAT</option>
                    <option value="ASVAB">ASVAB</option>
                    <option value="AP">AP Exam</option>
                    <option value="GED">GED</option>
                    <option value="TEAS">TEAS</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm text-slate-300 block mb-1">Score *</label>
                  <Input
                    type="number"
                    placeholder="e.g., 1400"
                    value={newScore.score}
                    onChange={(e) => setNewScore({...newScore, score: e.target.value})}
                    className="input-dark"
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-300 block mb-1">Date Taken *</label>
                  <Input
                    type="date"
                    value={newScore.date_taken}
                    onChange={(e) => setNewScore({...newScore, date_taken: e.target.value})}
                    className="input-dark"
                  />
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowAddScore(false)} className="border-slate-700">Cancel</Button>
                  <Button onClick={addTestScore} className="flex-1 bg-blue-600 hover:bg-blue-700">Add Score</Button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </AppLayout>
  );
}
